import React, { useEffect, useState } from 'react';
import { Container, Card, Table, Spinner, Alert, Button, Row, Col, Modal, Badge } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import api, { getPublicUploadsOrigin } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Footer from '../components/Footer';
import PortalProveedorPageHeader from '../components/PortalProveedorPageHeader';

const MisCotizaciones = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [searchCode, setSearchCode] = useState('');
  const [convocatoriasConGanador, setConvocatoriasConGanador] = useState(new Set());

  useEffect(() => {
    if (!isAuthenticated) {
      localStorage.setItem('redirectAfterLogin', '/mis-cotizaciones');
      navigate('/login');
      return;
    }
    load();
  }, [isAuthenticated]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/v1/cotizaciones/mine');
      const data = res.data || [];
      setItems(data);
      
      // Identificar convocatorias únicas
      const convocatoriasUnicas = new Set();
      data.forEach(cot => {
        if (cot.nro_convocatoria) {
          convocatoriasUnicas.add(cot.nro_convocatoria);
        }
      });
      
      // Verificar cada convocatoria si tiene ganador
      const ganadores = new Set();
      const verificaciones = Array.from(convocatoriasUnicas).map(async (nroConv) => {
        try {
          const resGanador = await api.get(`/api/v1/cotizaciones/convocatoria/${nroConv}/tiene-ganador`);
          if (resGanador.data?.tiene_ganador) {
            ganadores.add(nroConv);
            ganadores.add(String(nroConv));
            ganadores.add(Number(nroConv));
          }
        } catch (err) {
          console.error(`Error verificando ganador para convocatoria ${nroConv}:`, err);
        }
      });
      
      await Promise.all(verificaciones);
      setConvocatoriasConGanador(ganadores);
    } catch (e) {
      // Mostrar detalle del error si viene del backend
      const backendData = e.response?.data;
      const backendDetail = backendData?.detail ?? backendData?.message ?? backendData;
      const backendMessage = typeof backendDetail === 'string' 
        ? backendDetail 
        : Array.isArray(backendDetail)
          ? backendDetail.map((d) => (typeof d === 'string' ? d : JSON.stringify(d))).join(' | ')
          : backendDetail && typeof backendDetail === 'object'
            ? JSON.stringify(backendDetail)
            : undefined;
      const status = e.response?.status;
      console.error('Error al cargar mis cotizaciones:', { status, data: e.response?.data, error: e });
      setError(backendMessage ? `No se pudieron cargar sus postulaciones: ${backendMessage}` : 'No se pudieron cargar sus postulaciones.');
    } finally {
      setLoading(false);
    }
  };

  const sanitizeFragment = (value) => {
    if (!value) return '';
    const trimmed = String(value).trim().replace(/\s+/g, '_');
    return trimmed.replace(/[^a-zA-Z0-9_-]/g, '');
  };

  const fileUrl = (c, field) => {
    if (!c || !c[field]) return null;
    const folder = sanitizeFragment(c.COD_CONVOCATORIA || c.nro_convocatoria);
    return `${getPublicUploadsOrigin()}/uploads/cotizaciones/${folder}/${c[field]}`;
  };

  const evaluacionFileUrl = (c) => {
    if (!c || !c.evaluacion_ficha_calificacion_pdf) return null;
    const codCotizacion = c.cod_cotizacion || c.id_cotizacion;
    const folder = sanitizeFragment(codCotizacion);
    return `${getPublicUploadsOrigin()}/uploads/evaluaciones/${folder}/${c.evaluacion_ficha_calificacion_pdf}`;
  };

  const tieneEvaluacion = (c) => {
    // Verificar que realmente exista una evaluación con datos
    // No considerar solo evaluacion_seleccionado porque puede ser false
    return !!(c.evaluacion_marca || c.evaluacion_precio_ofertado || c.evaluacion_plazo_entrega || 
              c.evaluacion_validez_cotizacion || c.evaluacion_observaciones || c.evaluacion_ficha_calificacion_pdf ||
              c.evaluacion_fecha_registro);
  };

  const tieneGanadorEnConvocatoria = (nroConvocatoria) => {
    // Verificar si esta convocatoria tiene un ganador
    // Verificar tanto como número como string
    const tiene = convocatoriasConGanador.has(nroConvocatoria) || 
                  convocatoriasConGanador.has(String(nroConvocatoria)) ||
                  convocatoriasConGanador.has(Number(nroConvocatoria));
    return tiene;
  };

  const openResultados = (c) => {
    // Navegar a la página de resultados de la convocatoria
    navigate(`/resultados-convocatoria/${c.nro_convocatoria}`);
  };

  const openDetalle = async (c) => {
    setSelected(c);
    setShowModal(true);
    setDetalle(null);
    setDetalleLoading(true);
    try {
      const res = await api.get(`/api/v1/convocatorias/public/${c.nro_convocatoria}`);
      setDetalle(res.data);
    } catch (e) {
      console.error('No se pudo cargar la convocatoria:', e);
      setDetalle(null);
    } finally {
      setDetalleLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <>
      <PortalProveedorPageHeader
        title="Mis postulaciones"
        subtitle="Cotizaciones que ha enviado a convocatorias públicas."
      >
        <div className="d-flex flex-wrap gap-2 justify-content-md-end">
          <Button as={Link} to="/" variant="light" size="sm" className="shadow-sm">
            <i className="bi bi-house-door me-1" />
            Inicio
          </Button>
        </div>
      </PortalProveedorPageHeader>

      <Container className="py-4" style={{ maxWidth: 1200 }}>
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-4 gap-3">
        <div>
          <p className="text-muted mb-0 small">Busque por código y revise el detalle de cada envío.</p>
        </div>
        <div className="d-flex align-items-center gap-2 flex-grow-1 flex-lg-grow-0" style={{ minWidth: 0, maxWidth: '420px' }}>
          <input
            type="text"
            className="form-control"
            placeholder="Buscar por código de cotización"
            value={searchCode}
            onChange={(e) => setSearchCode(e.target.value)}
          />
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mb-3">{error}</Alert>
      )}

      <Card className="border-0 shadow-sm overflow-hidden">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0 portal-proveedor-table align-middle">
               <thead>
                 <tr>
                   <th className="border-0 py-3 px-3">Fecha</th>
                   <th className="border-0 py-3 px-3">Cód. Cotización</th>
                   <th className="border-0 py-3 px-3">Convocatoria</th>
                   <th className="border-0 py-3 px-3">NRO_PEDIDO</th>
                   <th className="border-0 py-3 px-3">Estado</th>
                 </tr>
               </thead>
              <tbody>
                 {items.length === 0 ? (
                   <tr>
                     <td className="py-4 px-3" colSpan={5}>No tienes postulaciones registradas.</td>
                   </tr>
                 ) : (
                  (items.filter((c) => {
                    if (!searchCode) return true;
                    const code = (c.cod_cotizacion || '').toString().toLowerCase();
                    return code.includes(searchCode.toLowerCase());
                  })).map((c) => (
                    <tr key={c.id_cotizacion}>
                      <td className="py-3 px-3">{c.fecha_registro ? new Date(c.fecha_registro).toLocaleString() : '-'}</td>
                      <td className="py-3 px-3"><code>{c.cod_cotizacion || '-'}</code></td>
                      <td className="py-3 px-3">{c.COD_CONVOCATORIA || '-'}</td>
                      <td className="py-3 px-3">{c.NRO_PEDIDO || '-'}</td>
                       <td className="py-3 px-3">
                         {c.evaluacion_seleccionado === true ? (
                           <Badge bg="warning" text="dark">
                             <i className="bi bi-trophy-fill me-1"></i>
                             Ganador
                           </Badge>
                         ) : tieneEvaluacion(c) ? (
                           <Badge bg="success">
                             <i className="bi bi-check-circle me-1"></i>
                             Evaluada
                           </Badge>
                         ) : (
                           <Badge bg="secondary">
                             <i className="bi bi-clock me-1"></i>
                             Pendiente
                           </Badge>
                         )}
                         {(() => {
                           // Verificar si hay ganador en la convocatoria
                           const tieneGanador = tieneGanadorEnConvocatoria(c.nro_convocatoria);
                           
                           // También verificar directamente si esta cotización es ganador
                           const esGanadorDirecto = c.evaluacion_seleccionado === true || 
                                                   c.evaluacion_seleccionado === 1 || 
                                                   c.evaluacion_seleccionado === "true" ||
                                                   String(c.evaluacion_seleccionado).toLowerCase() === 'true';
                           
                           // Verificar si alguna cotización de esta convocatoria es ganador
                           // (por si el usuario actual no es el ganador pero hay ganador en la convocatoria)
                           const hayGanadorEnConvocatoria = items.some(item => 
                             item.nro_convocatoria === c.nro_convocatoria &&
                             (item.evaluacion_seleccionado === true || 
                              item.evaluacion_seleccionado === 1 || 
                              item.evaluacion_seleccionado === "true" ||
                              String(item.evaluacion_seleccionado).toLowerCase() === 'true')
                           );
                           
                           // Mostrar botón si hay ganador en la convocatoria
                           const mostrarBoton = tieneGanador || esGanadorDirecto || hayGanadorEnConvocatoria;
                           
                           if (mostrarBoton) {
                             return (
                               <div className="mt-2">
                                 <Button onClick={() => openResultados(c)} size="sm" variant="success">
                                   <i className="bi bi-clipboard-check me-1"></i>
                                   Ver Resultados
                                 </Button>
                               </div>
                             );
                           }
                           return null;
                         })()}
                       </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </Container>
    <Footer />

    {/* Modal Detalle de Convocatoria y Archivos */}
    <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {selected?.COD_CONVOCATORIA || selected?.nro_convocatoria || 'Convocatoria'}
          {selected?.cod_cotizacion && (
            <small className="ms-3 text-muted">Cód. Cotización: <code>{selected.cod_cotizacion}</code></small>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {detalleLoading ? (
          <div className="text-center py-4"><Spinner animation="border" /></div>
        ) : (
          <>
            {detalle ? (
              <div className="mb-4">
                <Row className="g-3">
                  <Col md={6}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-box-seam me-2"></i>
                      <small className="text-muted me-2">Tipo:</small>
                      <span>{detalle.tipo_bien === 'S' ? 'Servicio' : detalle.tipo_bien === 'B' ? 'Bienes' : 'N/A'}</span>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-calendar3 me-2"></i>
                      <small className="text-muted me-2">Año:</small>
                      <span>{detalle.ano_eje || 'N/A'}</span>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-hash me-2"></i>
                      <small className="text-muted me-2">Nro Pedido:</small>
                      <span>{detalle.nro_pedido || 'N/A'}</span>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-info-circle me-2"></i>
                      <small className="text-muted me-2">Estado:</small>
                      <Badge bg="secondary">{detalle.estado || 'N/A'}</Badge>
                    </div>
                  </Col>
                </Row>
                {detalle.motivo_pedido && (
                  <div className="mt-3 p-2 border rounded">
                    <div className="d-flex align-items-center mb-2">
                      <i className="bi bi-file-text me-2"></i>
                      <strong>Motivo del Pedido</strong>
                    </div>
                    <div className="small">{detalle.motivo_pedido}</div>
                  </div>
                )}
              </div>
            ) : (
              <Alert variant="warning">No se pudieron cargar los detalles de la convocatoria.</Alert>
            )}

            <h6 className="fw-bold mb-2">Archivos enviados</h6>
            <ul className="mb-0">
              <li>
                Cotización: {selected?.cotizacion_pdf ? (
                  <a href={fileUrl(selected, 'cotizacion_pdf')} target="_blank" rel="noreferrer">Ver PDF</a>
                ) : (
                  <span className="text-muted">No adjunto</span>
                )}
              </li>
              <li>
                Declaración de Postor: {selected?.declaracion_postor_pdf ? (
                  <a href={fileUrl(selected, 'declaracion_postor_pdf')} target="_blank" rel="noreferrer">Ver PDF</a>
                ) : (
                  <span className="text-muted">No adjunto</span>
                )}
              </li>
              <li>
                Registro Nacional de Proveedores: {selected?.registro_nacional_proveedores_pdf ? (
                  <a href={fileUrl(selected, 'registro_nacional_proveedores_pdf')} target="_blank" rel="noreferrer">Ver PDF</a>
                ) : (
                  <span className="text-muted">No adjunto</span>
                )}
              </li>
              <li>
                Ficha Técnica: {selected?.ficha_tecnica_pdf ? (
                  <a href={fileUrl(selected, 'ficha_tecnica_pdf')} target="_blank" rel="noreferrer">Ver PDF</a>
                ) : (
                  <span className="text-muted">No adjunto</span>
                )}
              </li>
            </ul>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowModal(false)}>Cerrar</Button>
      </Modal.Footer>
    </Modal>

    </>
  );
};

export default MisCotizaciones;


